/**
 * AzurePickerBody — renders the picker's body for each discovery state:
 * loading, every named error (copy + recovery from `azurePickerErrors.ts`:
 * sign-in for an expired session / MFA / consent / a cancelled sign-in, Refresh
 * for a moved identity, a different account for an unverifiable record, Retry
 * for transient failures, none for Conditional Access), the partial-list
 * banner, the owner-mismatch banner over inert rows, zero-resources (with the
 * permission copy), no-search-match, and the tree. All state / selection logic
 * stays in the parent.
 */
import type { ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import type {
  AzureDiscoveredResource,
  AzureDiscoveryCompleteness,
  AzureDiscoveryError,
  AzureIdentityInfo,
} from '../../../types/azureDiscovery';
import { partialListText, PERMISSION_COPY, presentAzureError } from './azurePickerErrors';
import type { AzureTreeGroup } from './azurePickerFilter';
import { AzurePickerTree } from './AzurePickerTree';

interface AzurePickerBodyProps {
  isLoading: boolean;
  error?: AzureDiscoveryError;
  identity?: AzureIdentityInfo;
  completeness?: AzureDiscoveryCompleteness;
  /** The rows belong to another principal; they are shown inert until the refetch lands. */
  ownerMismatch: boolean;
  fullTreeEmpty: boolean;
  visibleTree: AzureTreeGroup[];
  search: string;
  multiSelect: boolean;
  /** Entry mode (C3.1): Enter and a double-click on a resource row confirm that row, even in the multi-select tree. */
  confirmOnRow?: boolean;
  selectedIds: Set<string>;
  focusedId?: string;
  disabledIds: Set<string>;
  chip: ReactNode;
  onSignIn: () => void;
  onSignInDifferent: () => void;
  onRetry: () => void;
  onToggleResource: (id: string, checked: boolean) => void;
  onToggleMany: (ids: string[], checked: boolean) => void;
  onFocus: (resource: AzureDiscoveredResource) => void;
  onConfirmResource: (resource: AzureDiscoveredResource) => void;
}

function CenteredState({ children }: { children: ReactNode }) {
  return <div className="flex h-full flex-col items-center justify-center gap-3 py-10 text-center">{children}</div>;
}

function Banner({ text, action, onAction, testId }: { text: string; action?: string; onAction?: () => void; testId: string }) {
  return (
    <div
      role="status"
      data-testid={testId}
      className="mb-2 flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs"
    >
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" aria-hidden="true" />
      <span className="flex-1">{text}</span>
      {action && onAction ? (
        <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={onAction}>
          {action}
        </Button>
      ) : null}
    </div>
  );
}

export function AzurePickerBody(props: AzurePickerBodyProps) {
  const { isLoading, error, identity, completeness, ownerMismatch, fullTreeEmpty, visibleTree, search } = props;

  if (isLoading) {
    return (
      <CenteredState>
        <RandomDotmSpinner size={28} />
        <p className="text-sm text-muted-foreground">Querying Azure Resource Graph…</p>
      </CenteredState>
    );
  }

  if (error) {
    const presentation = presentAzureError(error);
    const recover =
      presentation.recovery === 'sign-in'
        ? props.onSignIn
        : presentation.recovery === 'sign-in-different'
          ? props.onSignInDifferent
          : presentation.recovery === 'refresh' || presentation.recovery === 'retry'
            ? props.onRetry
            : undefined;
    const signIn = presentation.recovery === 'sign-in';
    return (
      <CenteredState>
        <p
          className={`max-w-md whitespace-pre-wrap break-words text-sm ${signIn ? 'text-muted-foreground' : 'text-destructive'}`}
          data-testid={signIn ? 'azure-picker-signin-message' : 'azure-picker-error'}
          data-error-code={error.code}
        >
          {presentation.title}
        </p>
        {presentation.showDetail && error.message && error.message !== presentation.title ? (
          <p className="max-w-md break-words text-xs text-muted-foreground" data-testid="azure-picker-error-detail">
            {error.message}
          </p>
        ) : null}
        <div className="flex items-center gap-2">
          {recover && presentation.action ? (
            <Button
              size="sm"
              variant={signIn ? 'default' : 'outline'}
              onClick={recover}
              data-testid={signIn ? 'azure-picker-signin' : 'azure-picker-recover'}
            >
              {presentation.action}
            </Button>
          ) : null}
          {!signIn ? props.chip : null}
        </div>
        {signIn ? (
          <p className="text-xs text-muted-foreground">A signed-in Azure CLI (“az login”) is auto-detected on Refresh.</p>
        ) : null}
      </CenteredState>
    );
  }

  const partial = completeness ? partialListText(completeness) : undefined;
  const banners = (
    <>
      {ownerMismatch ? (
        <Banner text="Your Azure account changed. Refresh to list its resources." action="Refresh" onAction={props.onRetry} testId="azure-picker-identity-banner" />
      ) : null}
      {partial ? <Banner text={partial} action="Retry" onAction={props.onRetry} testId="azure-picker-partial-banner" /> : null}
    </>
  );

  if (fullTreeEmpty) {
    // An empty (engine-filtered) tree still says WHY the list may be incomplete (C4A-03b).
    return (
      <div className="flex h-full flex-col">
        {banners}
        <CenteredState>
          <p className="text-sm text-muted-foreground">
            No resources found for {identity?.account ?? 'this account'}
            {identity?.tenantId ? ` (tenant ${identity.tenantId})` : ''}.
          </p>
          <p className="max-w-md text-xs text-muted-foreground">{PERMISSION_COPY}</p>
          {props.chip}
        </CenteredState>
      </div>
    );
  }

  if (visibleTree.length === 0) {
    return (
      <div className="flex h-full flex-col">
        {banners}
        <CenteredState>
          <p className="text-sm text-muted-foreground">No resources match “{search}”.</p>
        </CenteredState>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {banners}
      <ScrollArea className="h-full max-h-[46vh] pr-2">
        <AzurePickerTree
          groups={visibleTree}
          multiSelect={props.multiSelect}
          confirmOnRow={props.confirmOnRow}
          selectedIds={props.selectedIds}
          focusedId={props.focusedId}
          disabledIds={props.disabledIds}
          forceExpand={search.trim().length > 0}
          inert={ownerMismatch}
          onToggleResource={props.onToggleResource}
          onToggleMany={props.onToggleMany}
          onFocus={props.onFocus}
          onConfirmResource={props.onConfirmResource}
        />
      </ScrollArea>
    </div>
  );
}
